import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";
import { doc, onSnapshot } from "firebase/firestore";
import Feather from "@expo/vector-icons/Feather";
import Head from "expo-router/head";

import { PostCard } from "@/components/PostCard";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import CustomModal from "@/components/ui/CustomModal.web";
import { FormInput } from "@/components/ui/forms/FormInput";
import { SearchFiltersForm } from "@/components/search/SearchFilters.web";
import { useAuth } from "@/context/AuthContext";
import { firestore } from "@/firebase/web";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Post } from "@/types/post";
import { UserDocument } from "@/types/user";
import {
  hasActiveFilters,
  PostsSearchFilters,
  searchPosts,
} from "@/utils/omniSearch.web";

const TAB_BAR_HEIGHT = 40;

const emptyFilters = (): PostsSearchFilters => ({
  country: undefined,
  eventType: undefined,
  dateField: "createdAt",
  dateFrom: null,
  dateTo: null,
});

export default function SearchScreen() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const tintColor = useThemeColor({}, "tint");
  const stripesColor = useThemeColor({}, "stripes");
  const backgroundColor = useThemeColor({}, "background");
  const color = useThemeColor({}, "text");
  const headerBackground = useThemeColor({}, "tabBarBackground");
  const headerForeground = useThemeColor({}, "tabIconActive");

  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState<PostsSearchFilters>(emptyFilters());
  const [draftFilters, setDraftFilters] =
    useState<PostsSearchFilters>(emptyFilters());
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [results, setResults] = useState<Post[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setUserCountry(null);
      return;
    }
    const unsubscribe = onSnapshot(
      doc(firestore, "users", user.uid),
      (snap) => {
        const data = snap.exists() ? (snap.data() as UserDocument) : null;
        setUserCountry(data?.country ?? null);
      },
      (err) => {
        console.error("Failed to load user country for search:", err);
        setUserCountry(null);
      },
    );
    return () => unsubscribe();
  }, [user]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.eventType) n++;
    if (filters.dateFrom || filters.dateTo) n++;
    return n;
  }, [filters]);

  const runSearch = useCallback(
    async (nextFilters: PostsSearchFilters, nextText: string) => {
      const text = nextText.trim();
      if (!text && !hasActiveFilters(nextFilters)) {
        setResults(null);
        setErrorMessage(null);
        return;
      }
      setLoading(true);
      setErrorMessage(null);
      try {
        // Country is a hard profile scope, not a user-editable filter — inject
        // it into the query at search time rather than tracking it on the draft.
        const scopedFilters: PostsSearchFilters = {
          ...nextFilters,
          country: userCountry ?? undefined,
        };
        const { posts } = await searchPosts({
          filters: scopedFilters,
          searchText: text || undefined,
          fallbackCountry: userCountry,
        });
        setResults(posts);
      } catch (err: any) {
        console.error("Search query failed:", err);
        const code = err?.code;
        const raw: string = err?.message ?? "";
        const isIndexError =
          code === "failed-precondition" ||
          raw.includes("requires an index") ||
          raw.includes("console.firebase.google.com");
        setErrorMessage(
          isIndexError ? t("SEARCH_ERROR_INDEX") : t("SEARCH_ERROR_GENERIC"),
        );
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [userCountry, t],
  );

  const handleSubmitSearch = () => {
    runSearch(filters, searchText);
  };

  const openFilters = () => {
    setDraftFilters(filters);
    setFiltersOpen(true);
  };

  const handleApplyFilters = () => {
    const next = { ...draftFilters };
    setFilters(next);
    setFiltersOpen(false);
    runSearch(next, searchText);
  };

  const handleResetFilters = () => {
    setDraftFilters(emptyFilters());
  };

  const renderBody = () => {
    if (loading) {
      return (
        <ThemedView style={styles.centered}>
          <ActivityIndicator size="large" color={tintColor} />
        </ThemedView>
      );
    }
    if (errorMessage) {
      return (
        <ThemedView style={styles.centered}>
          <ThemedText style={{ textAlign: "center", padding: 16 }}>
            {errorMessage}
          </ThemedText>
        </ThemedView>
      );
    }
    if (results === null) {
      return (
        <ThemedView style={styles.centered}>
          <ThemedText style={{ textAlign: "center", opacity: 0.7 }}>
            {t("SEARCH_HINT")}
          </ThemedText>
        </ThemedView>
      );
    }
    if (results.length === 0) {
      return (
        <ThemedView style={styles.centered}>
          <ThemedText style={{ textAlign: "center" }}>
            {t("NO_RESULTS_FOUND")}
          </ThemedText>
        </ThemedView>
      );
    }
    return (
      <ThemedView
        style={[styles.listContainer, { backgroundColor: stripesColor }]}
      >
        <FlatList
          data={results}
          renderItem={({ item }) => <PostCard post={item} />}
          keyExtractor={(item) => item.id}
          style={[styles.flatList, { backgroundColor }]}
          contentContainerStyle={styles.flatListContent}
        />
      </ThemedView>
    );
  };

  return (
    <ThemedView style={styles.container}>
      <Head>
        <title>{t("PAGE_TITLE_SEARCH")}</title>
      </Head>
      <View style={[styles.header, { backgroundColor: headerBackground }]}>
        <ThemedText
          style={[styles.headerTitle, { color: headerForeground }]}
          numberOfLines={1}
        >
          {t("SEARCH")}
        </ThemedText>
      </View>
      <View style={[styles.searchRow, { borderBottomColor: stripesColor }]}>
        <View style={{ flex: 1 }}>
          <FormInput
            value={searchText}
            onChangeText={setSearchText}
            onSubmitEditing={handleSubmitSearch}
            returnKeyType="search"
            placeholder={t("SEARCH_USERNAME_PLACEHOLDER")}
            autoCapitalize="none"
            autoCorrect={false}
            testID="search-input"
          />
        </View>
        <TouchableOpacity
          onPress={openFilters}
          style={[styles.filterButton, { borderColor: stripesColor }]}
          testID="search-filters-button"
        >
          <Feather name="sliders" size={20} color={color} />
          {activeFilterCount > 0 && (
            <View
              style={[styles.badge, { backgroundColor: tintColor }]}
              testID="search-filters-badge"
            >
              <ThemedText style={styles.badgeText}>
                {activeFilterCount}
              </ThemedText>
            </View>
          )}
        </TouchableOpacity>
      </View>

      <View style={styles.body}>{renderBody()}</View>

      <CustomModal
        visible={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title={t("FILTERS")}
      >
        <SearchFiltersForm
          draft={draftFilters}
          onChange={setDraftFilters}
          onApply={handleApplyFilters}
          onReset={handleResetFilters}
        />
      </CustomModal>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    height: 60,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 60,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "700",
    textAlign: "center",
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderBottomWidth: 1,
  },
  filterButton: {
    width: 48,
    height: 48,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  badge: {
    position: "absolute",
    top: -4,
    right: -4,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
  },
  badgeText: {
    color: "#fff",
    fontSize: 11,
    fontWeight: "700",
  },
  body: {
    flex: 1,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
  },
  listContainer: {
    flex: 1,
    alignItems: "center",
  },
  flatList: {
    flex: 1,
    width: "100%",
    maxWidth: 680,
  },
  flatListContent: {
    paddingBottom: TAB_BAR_HEIGHT,
  },
});
